import { useRef, useState } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { votePoll } from '@api-client/api-client';
import type { PollRead, PollVoteBody, PollVoteResponse } from '@models/api/poll';
import type { SpaceSummary } from '@models/api/question';
import { cancelEntityProducers } from '@lib/cache/entityProducers';
import { getQuestionEntity } from '@lib/cache/questions';
import {
  beginPollWrite,
  isLatestPollWrite,
  pollWriteScopeId,
  readPollQuestion,
  settlePollWrite,
  showPoll,
} from '@lib/cache/polls';
import { retryOnceUnlessClientError } from '@lib/mutationRetry';
import { POLL_ERROR_CODES, pollErrorCode, pollErrorSpaceId } from '@lib/polls/pollErrors';
import { invalidateMembershipQueries } from '@lib/cache/spaces';
import { POLL_COPY } from '@components/polls/pollCopy';
import { PIN_REJECTED_ERROR, REQUEST_SENT_TOAST } from '@components/community/spaceAccessCopy';
import { joinFailureToast } from '@utils/membershipFailureCopy';
import { lightImpact } from '@utils/haptics';
import { toast } from '@hooks/use-toast';

/** What a tap did to the viewer's answer. */
export type PollAnswerOutcome = 'answered' | 'changed' | 'removed';

export interface UsePollVoteOptions {
  questionId: string;
  /** For the re-read after a 409 (the post page's read by slug; by uuid without one). */
  slug: string | null;
  /** The author always sees results, so taking an answer back hides nothing for them. */
  isAuthor: boolean;
  /**
   * The Space being viewed (the feed's): the one a non-member joins by answering (TECH-197 § The
   * API "Vote" `space_id`). Left out, the server picks the poll's home Space.
   */
  spaceId?: string | null;
  /** The post's Spaces as the viewer sees them: the names in B10, B11 and E17. */
  spaces?: readonly SpaceSummary[] | null;
}

interface PollVoteVariables {
  questionId: string;
  slug: string | null;
  isAuthor: boolean;
  optionIds: string[];
  /** The block the tap was made on; the cached one wins when there is one. */
  base: PollRead;
  spaceId?: string | null;
  /** E16's Join: the PIN the viewer typed. */
  accessCode?: string;
  /** E16's "Request access": join by request, the answer held. */
  requestAccess?: boolean;
  /** The answer saved signed out, cast after sign-in (CONTRACT § 10 N31, analytics only). */
  viaSignIn?: boolean;
}

interface PollVoteContext {
  ticket: number;
  /** The viewer's answer was held before this one went out (B11's toast says it once). */
  wasHeld: boolean;
}

/**
 * E16: the PIN drawer of the Space an answer must join first, holding the pick (TECH-197 § Voting
 * "Needs a PIN"). The card still shows the pick ticked while it is open; Cancel casts nothing.
 */
export interface PollPinRequest {
  optionIds: string[];
  /** The Space the server named; null when it named none. */
  spaceId: string | null;
  /** The line under the PIN field (E16b), or null. */
  error: string | null;
  viaSignIn: boolean;
}

/** E17: the answer cannot join any Space (unlisted, blocked, no join route); nothing was cast. */
export interface PollCannotJoin {
  space: { name: string; slug: string | null } | null;
}

/** The fields of the vote body this answer sends (none it does not need). */
const voteBody = ({
  optionIds,
  spaceId,
  accessCode,
  requestAccess,
  viaSignIn,
}: PollVoteVariables): PollVoteBody => ({
  option_ids: optionIds,
  ...(spaceId ? { space_id: spaceId } : {}),
  ...(accessCode !== undefined ? { access_code: accessCode } : {}),
  ...(requestAccess ? { request_access: true } : {}),
  ...(viaSignIn ? { via_sign_in: true } : {}),
});

/**
 * One queue per poll: answers go out one at a time, in order, so the server ends on the last tap;
 * End and Reopen join the same queue (`pollWriteScopeId`).
 */
export const pollVoteScopeId = (questionId: string) => pollWriteScopeId(questionId);

/** The block as cached now (what every card shows), or the one the tap was made on. */
const currentPoll = (queryClient: QueryClient, questionId: string, base: PollRead): PollRead =>
  getQuestionEntity(queryClient, questionId)?.poll ?? base;

/**
 * What the card shows from the tap until the server's block comes back. The server owns the
 * counts: a first answer keeps them hidden (they arrive with the block, and the bars grow in, F3),
 * a change keeps the last ones it sent. With no answer left the counts hide again, as for anyone
 * who has not answered, unless the viewer is the author (TECH-197 § Voting).
 */
export const predictPollAfterVote = (
  poll: PollRead,
  optionIds: string[],
  isAuthor: boolean,
): PollRead => {
  const next: PollRead = {
    ...poll,
    my_option_ids: optionIds,
    my_answer_held: optionIds.length > 0 && poll.my_answer_held,
  };
  if (optionIds.length > 0 || isAuthor || poll.is_closed) return next;
  return {
    ...next,
    results_visible: false,
    can_see_voters: false,
    voters_count: null,
    options: poll.options.map(option => ({ ...option, votes_count: null })),
  };
};

/** The poll block of a vote's response, without the join fields (they are not the poll's). */
const pollFromResponse = ({
  held: _held,
  held_space_id: _heldSpaceId,
  joined_space_id: _joinedSpaceId,
  ...poll
}: PollVoteResponse): PollRead => poll;

/**
 * Answering a standard poll as a signed-in viewer (TECH-197 § Voting; CONTRACT § 4.3): tap to
 * answer, another to move, yours again to take it back; with several answers each tap adds or
 * removes. The tap shows at once in every cached copy; the server's block replaces the guess when
 * it comes back; a failure goes back to what the server last confirmed with the E10 toast; 409
 * `POLL_CLOSED` re-reads the poll so its final results show (no toast). Taps go out one at a time
 * (the mutation scope) and only the newest one's outcome is shown (`pollWriteOrder`).
 *
 * A viewer who is not a member joins by answering (TECH-197 § Voting; CONTRACT § 9.1): joined →
 * B10's toast and their membership caches refreshed; held → B11's toast (once) and footer, results
 * shown (F1); `POLL_PIN_REQUIRED` → the PIN drawer (E16, `pin`), whose Join and "Request access"
 * send the pick again; `POLL_CANNOT_JOIN` → E17 on the card (`cannotJoin`), nothing cast.
 */
export const usePollVote = ({
  questionId,
  slug,
  isAuthor,
  spaceId = null,
  spaces = null,
}: UsePollVoteOptions) => {
  const queryClient = useQueryClient();
  const [pin, setPin] = useState<PollPinRequest | null>(null);
  const [cannotJoin, setCannotJoin] = useState<PollCannotJoin | null>(null);
  const spaceOf = (id: string | null) =>
    id ? (spaces?.find(space => space.id === id) ?? null) : null;

  /**
   * The last tap's answer and the cached block it was made on. A tap's guess reaches the cache only
   * once its `onMutate` runs, so a second tap before that would start from the block the card drew
   * and drop the first one; while the cache still holds the block the last tap saw, that tap's
   * answer is the one to build on. A failed tap forgets it: the failure puts back the very block
   * the tap saw, and the next tap must build on that, not on the answer that never went through.
   */
  const lastTapRef = useRef<{ seen: PollRead; optionIds: string[] } | null>(null);

  const mutation = useMutation<PollVoteResponse, unknown, PollVoteVariables, PollVoteContext>({
    scope: { id: pollVoteScopeId(questionId) },
    // A PUT of the full set: safe to send twice, so only a refusal is final.
    // At most one retry (~1 s): the guess on the card must not outlive a failure (E10).
    retry: retryOnceUnlessClientError,
    mutationFn: async variables => (await votePoll(variables.questionId, voteBody(variables))).data,
    onMutate: async ({ questionId: id, optionIds, isAuthor: author, base }) => {
      const wasHeld = currentPoll(queryClient, id, base).my_answer_held;
      const ticket = beginPollWrite(id, currentPoll(queryClient, id, base));
      // A read in flight would land after the guess and put the old block back.
      await cancelEntityProducers(queryClient, id);
      // A later tap may have started while this one waited: it writes its own guess.
      if (isLatestPollWrite(id, ticket)) {
        showPoll(
          queryClient,
          id,
          predictPollAfterVote(currentPoll(queryClient, id, base), optionIds, author),
        );
      }
      return { ticket, wasHeld };
    },
    onSuccess: (data, variables, context) => {
      const { questionId: id } = variables;
      const poll = pollFromResponse(data);
      if (settlePollWrite(id, context.ticket, poll).latest) showPoll(queryClient, id, poll);
      if (variables.accessCode !== undefined || variables.requestAccess) setPin(null);
      setCannotJoin(null);
      if (data.joined_space_id) {
        // B10: joined by answering, the answer counted.
        const name = spaceOf(data.joined_space_id)?.name;
        toast({
          title: name ? POLL_COPY.answerInJoined(name) : POLL_COPY.answerIn,
          variant: 'success',
          id: `poll-joined:${id}`,
        });
        invalidateMembershipQueries(queryClient, { spaceId: data.joined_space_id, scope: 'self' });
      } else if (data.held && !context.wasHeld) {
        // B11: a join request sent, the answer held until the Space lets them in. Said once.
        const name = spaceOf(data.held_space_id)?.name;
        toast(
          name
            ? { title: POLL_COPY.joinRequestSent(name), variant: 'info', id: `poll-held:${id}` }
            : { ...REQUEST_SENT_TOAST, variant: 'info', id: `poll-held:${id}` },
        );
        invalidateMembershipQueries(queryClient, {
          spaceId: data.held_space_id ?? undefined,
          scope: 'self',
        });
      } else if (variables.viaSignIn && !data.held) {
        // B8 (a held answer is B11's or says nothing, never "in": FE-1d review): the answer saved signed out is in (its PIN came after sign-in).
        toast({ title: POLL_COPY.answerIn, variant: 'success', id: `poll-answer-in:${id}` });
      }
    },
    onError: (error, variables, context) => {
      const { questionId: id, slug: questionSlug } = variables;
      // onMutate threw: nothing was written.
      if (!context) return;
      const { latest, confirmed } = settlePollWrite(id, context.ticket, null);
      // A later tap is on its way; its outcome decides what shows.
      if (!latest) return;
      lastTapRef.current = null;
      // Nothing was cast: the card goes back to what the server holds. A pick the PIN drawer holds
      // is drawn from `pin` (the card ticks it) until Join, Request access or Cancel.
      if (confirmed) showPoll(queryClient, id, confirmed);
      const code = pollErrorCode(error);
      if (code === POLL_ERROR_CODES.pinRequired) {
        setPin({
          optionIds: variables.optionIds,
          spaceId: pollErrorSpaceId(error),
          error: null,
          viaSignIn: variables.viaSignIn === true,
        });
        return;
      }
      if (code === POLL_ERROR_CODES.joinPinInvalid) {
        // E16b: today's line under the field; the drawer stays for another try.
        setPin(previous => previous && { ...previous, error: PIN_REJECTED_ERROR });
        return;
      }
      if (code === POLL_ERROR_CODES.joinPinRateLimited) {
        // Today's join copy; the drawer stays, "Request access" still works.
        toast({
          ...joinFailureToast(error, { accessCode: variables.accessCode }),
          variant: 'error',
        });
        return;
      }
      setPin(null);
      if (code === POLL_ERROR_CODES.cannotJoin) {
        const space = spaceOf(pollErrorSpaceId(error));
        setCannotJoin({ space: space ? { name: space.name, slug: space.slug } : null });
        return;
      }
      if (code === POLL_ERROR_CODES.closed) {
        // Closed before the answer reached it: show the final results (B3), not an error.
        void readPollQuestion(queryClient, { id, slug: questionSlug }).catch(() => undefined);
        return;
      }
      toast({ title: POLL_COPY.answerFailed, variant: 'error', id: `poll-vote-failed:${id}` });
    },
  });

  const { mutate } = mutation;

  /**
   * One tap on `optionId`. Builds on the newest answer: the cached block (what every card shows),
   * or the last tap's answer while its guess is not cached yet, so two fast taps on different
   * checkboxes both count. `poll` (the block the card drew) only stands in for an uncached one.
   * Fires the one light haptic of the moment after answering (F3, `lightImpact` as the app uses
   * it) on a first answer only: a change or a take back moves the bars without it (R13).
   */
  const answer = (poll: PollRead, optionId: string): PollAnswerOutcome => {
    const current = currentPoll(queryClient, questionId, poll);
    const lastTap = lastTapRef.current;
    const before = lastTap?.seen === current ? lastTap.optionIds : current.my_option_ids;
    const mine = new Set(before);
    const picked = mine.has(optionId);
    if (current.allows_multiple) {
      if (picked) mine.delete(optionId);
      else mine.add(optionId);
    } else {
      mine.clear();
      if (!picked) mine.add(optionId);
    }
    // In the options' order, whatever the order of the taps.
    const optionIds = current.options
      .filter(option => mine.has(option.id))
      .map(option => option.id);
    lastTapRef.current = { seen: current, optionIds };
    const outcome: PollAnswerOutcome =
      optionIds.length === 0 ? 'removed' : before.length === 0 ? 'answered' : 'changed';
    if (outcome === 'answered') void lightImpact();
    mutate({ questionId, slug, isAuthor, optionIds, base: poll, spaceId });
    return outcome;
  };

  /** Sends the held pick again from the PIN drawer: with the PIN (Join) or as a request. */
  const resend = (poll: PollRead, extra: { accessCode?: string; requestAccess?: boolean }) => {
    if (!pin) return;
    setPin({ ...pin, error: null });
    mutate({
      questionId,
      slug,
      isAuthor,
      optionIds: pin.optionIds,
      base: poll,
      spaceId: pin.spaceId ?? spaceId,
      viaSignIn: pin.viaSignIn,
      ...extra,
    });
  };

  return {
    answer,
    /** E16's state; null while no answer waits on a PIN. */
    pin,
    /** E17's state; null unless the last answer could not join. */
    cannotJoin,
    /** Opens E16 for an answer replayed after sign-in that came back `POLL_PIN_REQUIRED`. */
    requestPin: (request: { optionIds: string[]; spaceId: string | null }) => {
      setPin({ ...request, error: null, viaSignIn: true });
    },
    submitPin: (poll: PollRead, code: string) => {
      resend(poll, { accessCode: code });
    },
    requestAccess: (poll: PollRead) => {
      resend(poll, { requestAccess: true });
    },
    /**
     * Cancel: nothing cast, the pick goes (the card already shows what the server holds). Not while
     * a Join or Request access is in flight: that request lands anyway, so the drawer stays until it
     * answers (FE-1d review).
     */
    cancelPin: () => {
      if (mutation.isPending) return;
      setPin(null);
    },
    isSending: mutation.isPending,
  };
};
